import { cloneDeep } from '@apollo/client/utilities';
import { v4 } from 'uuid';
import { ParsedFileData } from '../../../../graphql/onboarding.graphql';
import { BinInput, BinStatusInput } from '../../../../types/bin';
import { CsvUploadType } from '../../../../types/csvUploadConfiguration';
import { Lot } from '../../../../types/lot/lot';
import { LotInput } from '../../../../types/lot/lot.input';
import { Product } from '../../../../types/product';
import { ProductMasterDataInput } from '../../../../types/product_master_data';
import { StockLocationInput } from '../../../../types/stock_location/stockLocation.input';
import { toMap } from '../../../../util/map.util';

export interface OnboardingEntities {
  stockLocations: Map<string, StockLocationInput>;
  bins: Map<string, BinInput>;
  binStatuses: Map<string, BinStatusInput>;
  products: Map<string, Product>;
  productMasterData: Map<string, ProductMasterDataInput>;
  lots: Map<string, Lot>;
}

export interface OnboardingFileImportData {
  file: {
    name: string;
    size: number;
  };
  type: CsvUploadType;
  data: OnboardingEntities;
}

export class OnboardingData {
  company: {
    id: string;
    name: string;
  } = { id: v4(), name: '' };
  users: Set<string> = new Set();
  manual: OnboardingEntities = {
    stockLocations: new Map(),
    productMasterData: new Map(),
    products: new Map(),
    binStatuses: new Map([
      [
        'AVAILABLE',
        new BinStatusInput({
          companyId: this.company.id,
          id: v4(),
          status: 'AVAILABLE',
          inboundAllowed: true,
          outboundAllowed: true,
          color: '#54A559',
        }),
      ],
    ]),
    lots: new Map(),
    bins: new Map(),
  };
  files: Map<string, OnboardingFileImportData> = new Map();

  private _summary: OnboardingEntities;
  public get summary() {
    return this._summary;
  }

  static fromJson(json: string) {
    const instance = new OnboardingData();

    const obj = JSON.parse(json);

    instance.company = obj.company;
    instance.users = new Set(obj.users || []);
    instance.manual = mapParsedFileData(obj.manual);
    instance.files = new Map(
      obj.files.map(([id, file]: [string, any]) => [id, { ...file, data: mapParsedFileData(file.data) }]),
    );

    return instance;
  }

  constructor() {
    this._summary = this.calculateSummary();
  }

  public toJson(): string {
    const serialized = {
      ...this,
      users: Array.from(this.users),
      manual: {
        stockLocations: Array.from(this.manual.stockLocations.values()),
        bins: Array.from(this.manual.bins.values()),
        binStatuses: Array.from(this.manual.binStatuses.values()),
        products: Array.from(this.manual.products.values()),
        productMasterData: Array.from(this.manual.productMasterData.values()),
        lots: Array.from(this.manual.lots.values()),
      },
      files: Array.from(this.files.entries()).map(([key, fileData]) => [
        key,
        {
          ...fileData,
          file: fileData.file,
          data: flattenOnboardingEntities(fileData.data),
        },
      ]),
    };

    return JSON.stringify(serialized);
  }

  private calculateSummary() {
    const result = {
      stockLocations: new Map<string, StockLocationInput>(),
      productMasterData: new Map<string, ProductMasterDataInput>(),
      products: new Map<string, Product>(),
      binStatuses: new Map<string, BinStatusInput>(),
      lots: new Map<string, Lot>(),
      bins: new Map<string, BinInput>(),
    };

    for (const [id, file] of this.files) {
      file.data.stockLocations.forEach(s => result.stockLocations.set(s.id, s));
      file.data.products.forEach(p => result.products.set(p.id, p));
      file.data.productMasterData.forEach(p => result.productMasterData.set(p.id, p));
      file.data.binStatuses.forEach(bs => result.binStatuses.set(bs.id, bs));
      file.data.bins.forEach(b => result.bins.set(b.id, b));
      file.data.lots.forEach(l => result.lots.set(l.id, l));
    }

    this.manual.stockLocations.forEach(s => result.stockLocations.set(s.id, s));
    this.manual.products.forEach(p => result.products.set(p.id, p));
    this.manual.productMasterData.forEach(p => result.productMasterData.set(p.id, p));
    this.manual.binStatuses.forEach(bs => result.binStatuses.set(bs.id, bs));
    this.manual.bins.forEach(b => result.bins.set(b.id, b));
    this.manual.lots.forEach(l => result.lots.set(l.id, l));

    return result;
  }

  import(file: OnboardingFileImportData) {
    this.files.set(v4(), file);

    const { stockLocations, productMasterData, binStatuses } = file.data;
    [...stockLocations.values()].forEach(s => this.manual.stockLocations.delete(s.id));
    [...binStatuses.values()].forEach(bs => this.manual.binStatuses.delete(bs.id));
    [...productMasterData.values()].forEach(p => this.manual.productMasterData.delete(p.id));

    return this.update();
  }

  update() {
    this._summary = this.calculateSummary();
    return this.copy();
  }

  private copy() {
    return cloneDeep(this);
  }
}

export function flattenOnboardingEntities(entities: OnboardingEntities): ParsedFileData {
  return {
    stockLocations: [...entities.stockLocations.values()].map(s => new StockLocationInput(s).forUpdate()),
    bins: [...entities.bins.values()].map(b => b.forUpdate()),
    binStatuses: [...entities.binStatuses.values()].map(bs => bs.forUpdate()),
    products: [...entities.products.values()].map(p => p.forUpdate()),
    productMasterData: [...entities.productMasterData.values()].map(p => p.forUpdate()),
    lots: [...entities.lots.values()].map(l => new LotInput(l).forUpdate()),
  };
}

export function mapParsedFileData(parsed: ParsedFileData): OnboardingEntities {
  return {
    stockLocations: new Map(
      toMap(
        parsed.stockLocations.map(s => new StockLocationInput(s)),
        'id',
      ),
    ),
    bins: new Map(
      toMap(
        parsed.bins.map(b => new BinInput(b)),
        'id',
      ),
    ),
    binStatuses: new Map(
      toMap(
        parsed.binStatuses.map(bs => new BinStatusInput(bs)),
        'id',
      ),
    ),
    products: new Map(
      toMap(
        parsed.products.map(p => new Product(p)),
        'id',
      ),
    ),
    productMasterData: new Map(
      toMap(
        parsed.productMasterData.map(p => new ProductMasterDataInput(p)),
        'id',
      ),
    ),
    lots: new Map(
      toMap(
        parsed.lots.map(l => new Lot(l)),
        'id',
      ),
    ),
  };
}
